// Lexical utilities for the tree-sitter syntax-witness pass: a DIALECT-AWARE,
// length-preserving neutralizer, a lightweight tokenizer, and a capped
// Damerau-Levenshtein distance.
//
// The neutralizer must be dialect-aware (CK-C): Oracle `q'[...]'` alternative
// quoting is a named hazard — the naive single-quote scanner would treat the
// inner `'` as a close and mis-balance the rest of the statement. Output is
// LENGTH-PRESERVING (quoted content → runs of `x`, comments → spaces, newlines
// kept) so every offset in the projection maps 1:1 back to the source.

import type { SqlEngine } from '@/features/sql-grammar/model';

export type SyntaxTokenType = 'word' | 'op' | 'punct';

export type SyntaxToken = {
  readonly text: string;
  readonly start: number;
  readonly end: number;
  readonly type: SyntaxTokenType;
};

const OP_CHARS = new Set('=<>!+-*/%|:^~&'.split(''));

// Digits count as word STARTS: numeric literals then tokenize as plain `word`
// tokens (never `punct`), so the structural dangling-operator rule can't
// misread `= 5` as an operator followed by structural punctuation.
function isWordStart(c: string): boolean {
  return /[A-Za-z0-9_$#]/.test(c);
}
function isWordChar(c: string): boolean {
  return /[A-Za-z0-9_$#]/.test(c);
}

/** Consume an Oracle `q'<delim>...<delim>'` literal starting at `i` (the `q`/`Q`,
 *  possibly after an `n`/`N`). Returns the index just past the closing quote, or
 *  -1 if this is not a q-quote. */
function scanOracleQQuote(text: string, i: number): number {
  const c = text[i];
  const q = c === 'q' || c === 'Q';
  const nq =
    (c === 'n' || c === 'N') && (text[i + 1] === 'q' || text[i + 1] === 'Q');
  if (!q && !nq) return -1;
  const qIdx = nq ? i + 1 : i;
  if (text[qIdx + 1] !== "'") return -1;
  const open = text[qIdx + 2];
  if (open === undefined) return -1;
  const close =
    open === '[' ? ']' : open === '{' ? '}' : open === '(' ? ')' : open === '<' ? '>' : open;
  let j = qIdx + 3;
  while (j < text.length) {
    if (text[j] === close && text[j + 1] === "'") return j + 2;
    j += 1;
  }
  return text.length;
}

/**
 * Project `text` to a code-only, length-preserving string under `engine`:
 * every string literal, quoted identifier and comment becomes `x`/space filler.
 * `unterminated` is true when a quoted region or block comment never closed.
 */
export function neutralize(
  text: string,
  engine: SqlEngine,
): { readonly code: string; readonly unterminated: boolean } {
  const supportsQQuote = engine === 'oracle';
  const supportsHashComment = engine === 'mysql';
  // MySQL string literals ('…' and "…") honour C-style backslash escapes by
  // default (NO_BACKSLASH_ESCAPES sql_mode — which turns this off — is not
  // modeled anywhere in the codebase, matching the tree-sitter mysql grammar).
  // Without this, `'don\'t )'` closes at `\'` and leaks a bare `)` into the
  // neutralized code → a phantom structural witness on valid SQL (F2). Backtick
  // identifiers escape by doubling, not backslash, so they are excluded.
  const supportsBackslashEscape = engine === 'mysql';
  let out = '';
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i];
    // Oracle alternative quoting `q'[...]'` — check before the plain `'` path.
    if (supportsQQuote) {
      const end = scanOracleQQuote(text, i);
      if (end >= 0) {
        for (let k = i; k < end && k < n; k += 1) out += text[k] === '\n' ? '\n' : 'x';
        if (end > n) return { code: out, unterminated: true };
        i = end;
        continue;
      }
    }
    // Line comment `-- …`
    if (c === '-' && text[i + 1] === '-') {
      while (i < n && text[i] !== '\n') { out += ' '; i += 1; }
      continue;
    }
    // MySQL `# …` line comment
    if (supportsHashComment && c === '#') {
      while (i < n && text[i] !== '\n') { out += ' '; i += 1; }
      continue;
    }
    // Block comment `/* … */`
    if (c === '/' && text[i + 1] === '*') {
      out += '  '; i += 2;
      while (i + 1 < n && !(text[i] === '*' && text[i + 1] === '/')) {
        out += text[i] === '\n' ? '\n' : ' '; i += 1;
      }
      if (i + 1 < n) { out += '  '; i += 2; continue; }
      while (i < n) { out += text[i] === '\n' ? '\n' : ' '; i += 1; }
      return { code: out, unterminated: true };
    }
    // Quoted regions: '…' (all), "…" (all), `…` (mysql/sqlite), [ … ] (sqlite id).
    const quote =
      c === "'" || c === '"' || c === '`'
        ? c
        : c === '[' && engine === 'sqlite'
          ? ']'
          : null;
    if (quote) {
      const openChar = c;
      const backslashEscapes = supportsBackslashEscape && (openChar === "'" || openChar === '"');
      out += 'x'; i += 1;
      while (i < n) {
        // Backslash escapes the following char (mysql `'…\'…'`) — consume both
        // as filler so an escaped delimiter never reads as a close.
        if (backslashEscapes && text[i] === '\\') {
          out += 'x'; i += 1;
          if (i >= n) return { code: out, unterminated: true };
          out += text[i] === '\n' ? '\n' : 'x'; i += 1;
          continue;
        }
        if (text[i] === quote) {
          // Doubled delimiter = escaped (not for bracket ids).
          if (openChar !== '[' && text[i + 1] === quote) { out += 'xx'; i += 2; continue; }
          out += 'x'; i += 1; break;
        }
        out += text[i] === '\n' ? '\n' : 'x'; i += 1;
        if (i >= n) return { code: out, unterminated: true };
      }
      continue;
    }
    out += c; i += 1;
  }
  return { code: out, unterminated: false };
}

/** Tokenize a NEUTRALIZED (code-only) string into words, operator runs and the
 *  structural punctuation the witness logic navigates. Offsets index the source. */
export function tokenize(code: string): SyntaxToken[] {
  const toks: SyntaxToken[] = [];
  let i = 0;
  const n = code.length;
  while (i < n) {
    const c = code[i];
    if (/\s/.test(c)) { i += 1; continue; }
    if (isWordStart(c)) {
      const start = i;
      while (i < n && isWordChar(code[i])) i += 1;
      toks.push({ text: code.slice(start, i), start, end: i, type: 'word' });
      continue;
    }
    if (OP_CHARS.has(c)) {
      const start = i;
      while (i < n && OP_CHARS.has(code[i])) i += 1;
      toks.push({ text: code.slice(start, i), start, end: i, type: 'op' });
      continue;
    }
    toks.push({ text: c, start: i, end: i + 1, type: 'punct' });
    i += 1;
  }
  return toks;
}

/** Damerau-Levenshtein (optimal string alignment) capped at 1: returns the true
 *  distance when ≤ 1, else 2. Case-insensitive. Fast-rejects on length gap > 1. */
export function isDamerauDistanceOne(a: string, b: string): boolean {
  const x = a.toUpperCase();
  const y = b.toUpperCase();
  if (x === y) return false;
  if (Math.abs(x.length - y.length) > 1) return false;
  const la = x.length;
  const lb = y.length;
  // At most one edit: same length → one substitution or one transposition;
  // length diff 1 → one insertion/deletion.
  if (la === lb) {
    let diffs = 0;
    let firstDiff = -1;
    for (let k = 0; k < la; k += 1) {
      if (x[k] !== y[k]) { diffs += 1; if (firstDiff < 0) firstDiff = k; }
      if (diffs > 2) return false;
    }
    if (diffs === 1) return true;
    if (diffs === 2) {
      // Adjacent transposition only.
      return (
        firstDiff + 1 < la &&
        x[firstDiff] === y[firstDiff + 1] &&
        x[firstDiff + 1] === y[firstDiff]
      );
    }
    return false;
  }
  // Length differs by exactly 1 — the shorter must be the longer minus one char.
  const shorter = la < lb ? x : y;
  const longer = la < lb ? y : x;
  let si = 0;
  let li = 0;
  let skipped = false;
  while (si < shorter.length && li < longer.length) {
    if (shorter[si] === longer[li]) { si += 1; li += 1; continue; }
    if (skipped) return false;
    skipped = true;
    li += 1;
  }
  return true;
}
